import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading, EmptyState } from "@/components/States";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueCard, CityCard } from "@/components/Cards";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { onImgError } from "@/lib/images";

const HERO = "https://images.unsplash.com/photo-1508863702330-2eece3ea08cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function HubShell({ eyebrow, title, description, path, children }) {
  return (
    <>
      <Seo title={`${title} | Hotelstrips`} description={description} image={HERO} path={path} />
      <section className="relative py-20 sm:py-24 flex items-center">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
          <div className="overline text-primary mb-3">{eyebrow}</div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter text-white max-w-3xl leading-tight">{title}</h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">{description}</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={[{ label: eyebrow }]} />
        {children}
      </div>
    </>
  );
}

export function VenuesHubPage() {
  const { data, loading } = useFetch("/hub/venues", []);
  const venues = data?.venues || [];
  const groups = {};
  venues.forEach((v) => {
    const k = v.city_name || "Other";
    (groups[k] = groups[k] || []).push(v);
  });
  const cityNames = Object.keys(groups).sort();
  return (
    <HubShell eyebrow="Venues" title="Stadiums & arenas we cover" path="/venues"
      description="Every venue with Hotelstrips stay-area intelligence — transit, airports and the best neighborhoods for gameday.">
      {loading ? <Loading /> : venues.length ? (
        <div data-testid="venues-hub-grid" className="space-y-12">
          {cityNames.map((cn) => (
            <div key={cn} data-testid={`venues-group-${cn.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <h2 className="font-heading text-lg font-bold text-ink mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> {cn}
              </h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {groups[cn].map((v) => <VenueCard key={v.slug} venue={v} />)}
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="No venues yet" description="Check back soon." />}
    </HubShell>
  );
}

export function DestinationsHubPage() {
  const { data, loading } = useFetch("/hub/destinations", []);
  const cities = data?.destinations || [];
  return (
    <HubShell eyebrow="Destinations" title="Cities built for a sports weekend" path="/destinations"
      description="Explore every market we cover — where to stay, how to get around and what's on around the game.">
      {loading ? <Loading /> : cities.length ? (
        <div data-testid="destinations-hub-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cities.map((c) => <CityCard key={c.slug} city={c} />)}
        </div>
      ) : <EmptyState title="No destinations yet" description="Check back soon." />}
    </HubShell>
  );
}

export function GuidesHubPage() {
  const { data, loading } = useFetch("/content/guides?published=true", []);
  const guides = Array.isArray(data) ? data : (data?.items || []);
  return (
    <HubShell eyebrow="Guides" title="Editorial travel guides" path="/guides"
      description="Practical, opinionated guides to planning the perfect trip around the game, tournament or event.">
      {loading ? <Loading /> : guides.length ? (
        <div data-testid="guides-hub-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {guides.map((g) => (
            <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`}
              className="group rounded-xl overflow-hidden bg-card border border-black/5 card-shadow transition-transform duration-300 hover:-translate-y-1">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={g.hero_image} alt={g.title} loading="lazy" onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="overline text-primary mb-2">{g.category}</div>
                <h3 className="font-heading text-lg font-bold text-ink leading-tight">{g.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      ) : <EmptyState title="No guides yet" description="Check back soon." />}
    </HubShell>
  );
}
